import * as React from 'react';

import { DataSourceTable } from 'src/constants/dataSourceTable';
import { AdvancedOption } from 'src/containers/ProjectVirtualGraphPreviewDialogV2/components/AdvancedOptionsV2';
import { DataSource } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraphField,
  ProjectVirtualGraphOptions,
  defaultProjectVirtualGraphOptions,
} from 'src/store/state/projects';

// Types for VG table/query selection
export type VgTableMetadata = {
  tableName: string;
  fields: ProjectVirtualGraphField[];
  rowCount?: number;
  editedName?: string;
  editedFieldLabels?: Record<string, string>;
};

export type VgCustomQuery = {
  query: string;
  name: string;
  fields?: ProjectVirtualGraphField[];
  rowCount?: number;
};

export type VgPreviewItem = {
  type: 'table' | 'query';
  name: string;
} | null;

export type VirtualGraphDialogState = {
  selectedDataSource: DataSource | null;
  setSelectedDataSource: (dataSource: DataSource | null) => void;
  selectedDataSourceType: string | null;
  setSelectedDataSourceType: (type: string | null) => void;
  checkedVgTables: DataSourceTable[];
  setCheckedVgTables: React.Dispatch<React.SetStateAction<DataSourceTable[]>>;
  vgTableMetadata: Record<string, VgTableMetadata>;
  setVgTableMetadata: React.Dispatch<
    React.SetStateAction<Record<string, VgTableMetadata>>
  >;
  customSqlQueries: VgCustomQuery[];
  setCustomSqlQueries: React.Dispatch<React.SetStateAction<VgCustomQuery[]>>;
  vgPreviewItem: VgPreviewItem;
  setVgPreviewItem: (item: VgPreviewItem) => void;
  virtualGraphOptions: ProjectVirtualGraphOptions;
  setVirtualGraphOptions: React.Dispatch<
    React.SetStateAction<ProjectVirtualGraphOptions>
  >;
  advancedVgOptions: AdvancedOption[];
  setAdvancedVgOptions: React.Dispatch<React.SetStateAction<AdvancedOption[]>>;
  resetVgState: () => void;
};

export const createVgStateDefaults = (): VirtualGraphDialogState => ({
  selectedDataSource: null,
  setSelectedDataSource: () => {},
  selectedDataSourceType: null,
  setSelectedDataSourceType: () => {},
  checkedVgTables: [],
  setCheckedVgTables: () => {},
  vgTableMetadata: {},
  setVgTableMetadata: () => {},
  customSqlQueries: [],
  setCustomSqlQueries: () => {},
  vgPreviewItem: null,
  setVgPreviewItem: () => {},
  virtualGraphOptions: defaultProjectVirtualGraphOptions,
  setVirtualGraphOptions: () => {},
  advancedVgOptions: [],
  setAdvancedVgOptions: () => {},
  resetVgState: () => {},
});

export const useVirtualGraphDialogState = (): VirtualGraphDialogState => {
  const [selectedDataSource, setSelectedDataSource] =
    React.useState<DataSource | null>(null);
  const [selectedDataSourceType, setSelectedDataSourceType] = React.useState<
    string | null
  >(null);
  const [checkedVgTables, setCheckedVgTables] = React.useState<
    DataSourceTable[]
  >([]);
  const [vgTableMetadata, setVgTableMetadata] = React.useState<
    Record<string, VgTableMetadata>
  >({});
  const [customSqlQueries, setCustomSqlQueries] = React.useState<
    VgCustomQuery[]
  >([]);
  const [vgPreviewItem, setVgPreviewItem] = React.useState<VgPreviewItem>(null);
  const [virtualGraphOptions, setVirtualGraphOptions] =
    React.useState<ProjectVirtualGraphOptions>(
      defaultProjectVirtualGraphOptions
    );
  const [advancedVgOptions, setAdvancedVgOptions] = React.useState<
    AdvancedOption[]
  >([{ property: '', value: '' }]);

  const resetVgState = React.useCallback(() => {
    setSelectedDataSource(null);
    setSelectedDataSourceType(null);
    setCheckedVgTables([]);
    setVgTableMetadata({});
    setCustomSqlQueries([]);
    setVgPreviewItem(null);
    setVirtualGraphOptions(defaultProjectVirtualGraphOptions);
    setAdvancedVgOptions([{ property: '', value: '' }]);
  }, []);

  return React.useMemo(
    () => ({
      selectedDataSource,
      setSelectedDataSource,
      selectedDataSourceType,
      setSelectedDataSourceType,
      checkedVgTables,
      setCheckedVgTables,
      vgTableMetadata,
      setVgTableMetadata,
      customSqlQueries,
      setCustomSqlQueries,
      vgPreviewItem,
      setVgPreviewItem,
      virtualGraphOptions,
      setVirtualGraphOptions,
      advancedVgOptions,
      setAdvancedVgOptions,
      resetVgState,
    }),
    [
      selectedDataSource,
      selectedDataSourceType,
      checkedVgTables,
      vgTableMetadata,
      customSqlQueries,
      vgPreviewItem,
      virtualGraphOptions,
      advancedVgOptions,
      resetVgState,
    ]
  );
};
